import type { PaginatedMeta, PaginationParams } from './client';
import { request, toQuery } from './client';

export type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export type PaymentMethod = 'offline';

export type SessionProgress = {
	scheduled: number;
	completed: number;
	total: number;
	remaining: number;
};

export type ActivityProgress = {
	completed: number;
	total: number;
	percentage: number;
	completed_activity_ids: number[];
};

export type Booking = {
	id: number;
	client_id: number;
	program_id: number;
	enrollment_id: number;
	client_name: string;
	program_title: string;
	status: BookingStatus;
	payment_method: PaymentMethod;
	payment_status: string;
	amount: number;
	currency: string;
	transaction_id: string | null;
	notes: string | null;
	session_progress: SessionProgress;
	activity_progress: ActivityProgress;
	paid_at: string | null;
	created_at: string;
	updated_at: string | null;
};

export type OrdersListResponse = PaginatedMeta & { orders: Booking[] };

export type OrdersListParams = PaginationParams & {
	client_id?: number;
	program_id?: number;
	status?: BookingStatus;
	search?: string;
	from_date?: string;
	to_date?: string;
	context?: 'view' | 'edit';
};

export type CreateBookingData = {
	client_id: number;
	program_id: number;
	status?: 'pending' | 'confirmed';
	payment_method?: PaymentMethod;
	amount?: number;
	currency?: string;
	notes?: string;
};

export type OrderBulkAction = 'delete' | 'confirm' | 'cancel' | 'reactivate';

export type OrderBulkResult = {
	action: OrderBulkAction;
	processed: number;
	failed: number;
	results: Array<{ id: number; success: boolean; error?: string }>;
};

export const ordersApi = {
	list: (params?: OrdersListParams) =>
		request<OrdersListResponse>('/orders' + toQuery(params)),

	get: (id: number) => request<Booking>(`/orders/${id}`),

	create: (data: CreateBookingData) =>
		request<Booking>('/orders', { method: 'POST', data }),

	confirm: (id: number, transaction_id?: string) =>
		request<Booking>(`/orders/${id}/confirm`, {
			method: 'POST',
			data: transaction_id ? { transaction_id } : {},
		}),

	cancel: (id: number) =>
		request<Booking>(`/orders/${id}/cancel`, { method: 'POST', data: {} }),

	reactivate: (id: number) =>
		request<Booking>(`/orders/${id}/reactivate`, { method: 'POST', data: {} }),

	delete: (id: number) =>
		request<{ deleted: boolean; id: number }>(`/orders/${id}`, {
			method: 'DELETE',
		}),

	bulk: (action: OrderBulkAction, ids: number[]) =>
		request<OrderBulkResult>('/orders/bulk', {
			method: 'POST',
			data: { action, ids },
		}),
};
